import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function IncidentFormsModal({ serverId, serviceUserId, onClose }) {
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchIncidents = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'incidentForms');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                loaded.sort((a, b) => {
                    const getMs = (val) => {
                        if (!val) return 0;
                        if (typeof val.toDate === 'function') return val.toDate().getTime();
                        if (val.seconds) return val.seconds * 1000;
                        return new Date(val).getTime() || 0;
                    };
                    const dateA = getMs(a.incidentDate || a.date || a.createdAt);
                    const dateB = getMs(b.incidentDate || b.date || b.createdAt);
                    return dateB - dateA; // Newest first
                });
                
                setIncidents(loaded);
            } catch (err) {
                console.error("Error fetching incidents:", err);
                setError("Failed to load incident forms.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchIncidents();
        }
    }, [serverId, serviceUserId]);

    const getSeverityBadge = (severity) => {
        const s = (severity || '').toLowerCase();
        if (s.includes('high') || s.includes('severe') || s.includes('critical')) {
            return { bg: '#fee2e2', text: '#991b1b', border: '#fecaca', label: 'HIGH SEVERITY' };
        }
        if (s.includes('medium') || s.includes('moderate')) {
            return { bg: '#fef3c7', text: '#92400e', border: '#fde68a', label: 'MEDIUM SEVERITY' };
        }
        if (s.includes('low') || s.includes('minor')) {
            return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0', label: 'LOW SEVERITY' };
        }
        return { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db', label: 'NOT SPECIFIED' };
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Incident Forms</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Review accidents, incidents, and near-misses</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading incident forms...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : incidents.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚨</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Incidents</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no incident forms recorded for this service user.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {incidents.map((incident, index) => {
                                const title = incident.title || incident.incidentType || incident.type || 'Incident Report';
                                const details = incident.description || incident.details || incident.notes || 'No details provided';
                                const badge = getSeverityBadge(incident.severity || incident.riskLevel);
                                
                                return (
                                    <div key={incident.id || index} style={styles.recordCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{title}</h3>
                                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                    Reported by: {incident.reportedBy || incident.author || incident.createdBy || 'Unknown User'}
                                                </div>
                                            </div>
                                            <span style={{ 
                                                display: 'inline-block', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 'bold',
                                                backgroundColor: badge.bg, color: badge.text, border: `1px solid ${badge.border}`
                                            }}>
                                                {badge.label}
                                            </span>
                                        </div>
                                        
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Date of Incident</label>
                                                <div style={styles.fieldValue}>{incident.incidentDate || incident.date || incident.createdAt ? new Date((incident.incidentDate || incident.date || incident.createdAt)?.seconds ? (incident.incidentDate || incident.date || incident.createdAt).seconds * 1000 : (incident.incidentDate || incident.date || incident.createdAt)).toLocaleString() : 'N/A'}</div>
                                            </div>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Status</label>
                                                <div style={styles.fieldValue}>{incident.status || incident.stage || 'Under Review'}</div>
                                            </div>
                                        </div>
                                        
                                        <div style={{ marginTop: '16px' }}>
                                            <label style={styles.fieldLabel}>Incident Details</label>
                                            <div style={{ fontSize: '13px', color: '#4b5563', whiteSpace: 'pre-wrap' }}>{details}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' },
    fieldValue: { fontSize: '14px', color: '#111827' }
};
